import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Select,
  Text as ChakraText,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  Breadcrumbs,
  Card,
  GridTable,
  Pagination,
  Text,
} from '@diamond/shared/ui';
import { showToast } from '@diamond/shared/ui';
import { AdminLayout } from '@diamond/sol-admin/components';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useCallback, useMemo, useRef, useState } from 'react';

import { CollectionFilterBar } from '../components/collection-filter-bar';
import { CreateLHCModal } from '../components/create-lhc-modal';
import { EditLHCModal } from '../components/edit-lhc-modal';
import { SyncInfo } from '../components/sync-info';
import { useDeleteLHCMutation, useLHCExportMutation } from '../hooks';
import { useLhcListPage } from '../hooks/use-lhc-list-page';
import type { LHCRowData } from '../types';

const links = [
  { title: 'Beranda', link: '/' },
  { title: 'Collection Management', link: '/collection-management' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type ActionCellRendererParams = {
  onExportLHC: (id: string) => Promise<unknown>;
  isExporting: boolean;
  onEditLHC: (row: LHCRowData) => void;
  onDeleteLHC: (row: LHCRowData) => void;
};

function ActionCellRenderer(
  params: ActionCellRendererParams & ICellRendererParams<LHCRowData>
): JSX.Element {
  const handleExport = async () => {
    const data = params.data;
    if (params.isExporting) return;
    if (params.onExportLHC && data?.id) {
      try {
        await params.onExportLHC(data.id);
      } catch {
        // Error toast is handled in useLHCExportMutation onError
      }
    }
  };

  const handleEdit = () => {
    const data = params.data;
    if (data && params.onEditLHC) {
      params.onEditLHC(data);
    }
  };

  const handleDelete = () => {
    const data = params.data;
    if (data && params.onDeleteLHC) {
      params.onDeleteLHC(data);
    }
  };

  return (
    <Flex
      align="center"
      h="100%"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Menu>
        <MenuButton
          as={Button}
          size="sm"
          variant="outline"
          borderColor="#0000a3"
          color="#0000a3"
          borderRadius="md"
          rightIcon={<KeyboardArrowDownIcon fontSize="small" />}
          _hover={{ bgColor: 'blue.50' }}
          _active={{ bgColor: 'blue.100' }}
        >
          Action
        </MenuButton>
        <Portal>
          <MenuList
            zIndex={2000}
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
            letterSpacing="0%"
          >
            <MenuItem onClick={handleExport} isDisabled={params.isExporting}>
              <Flex align="center" gap={2}>
                <Box
                  as="span"
                  display="inline-flex"
                  w={4}
                  h={4}
                  flexShrink={0}
                  alignItems="center"
                  justifyContent="center"
                >
                  <FileDownloadIcon style={{ fontSize: 16 }} />
                </Box>
                Export .xlsx
              </Flex>
            </MenuItem>
            <MenuItem onClick={handleEdit}>
              <Flex align="center" gap={2}>
                <Box
                  as="span"
                  display="inline-flex"
                  w={4}
                  h={4}
                  flexShrink={0}
                  alignItems="center"
                  justifyContent="center"
                >
                  <EditIcon style={{ fontSize: 16 }} />
                </Box>
                Edit
              </Flex>
            </MenuItem>
            <MenuItem color="red" onClick={handleDelete}>
              <Flex align="center" gap={2}>
                <Box
                  as="span"
                  display="inline-flex"
                  w={4}
                  h={4}
                  flexShrink={0}
                  alignItems="center"
                  justifyContent="center"
                >
                  <DeleteForeverIcon style={{ fontSize: 16 }} />
                </Box>
                Delete
              </Flex>
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
    </Flex>
  );
}

export function CollectionManagement() {
  const {
    draftFilters,
    firstPage,
    handleCabangChange,
    handleClearSearch,
    handleDateFilter,
    handlePlantChange,
    handleSearch,
    handleSearchChange,
    isLoading,
    lastPage,
    methods,
    navigateHome,
    navigateToDetail,
    nextPage,
    pageSize,
    plantOptions,
    previousPage,
    refetch,
    refetchCollectionPlants,
    searchValue,
    setPageSize,
    tableData,
    totalPages,
    validCurrentPage,
    updateDateParams,
  } = useLhcListPage();

  // Edit LHC modal state
  const {
    isOpen: isEditLHCOpen,
    onOpen: onEditLHCOpen,
    onClose: onEditLHCClose,
  } = useDisclosure();
  const [selectedLHCForEdit, setSelectedLHCForEdit] =
    useState<LHCRowData | null>(null);

  // Create LHC modal state
  const {
    isOpen: isCreateLHCOpen,
    onOpen: onCreateLHCOpen,
    onClose: onCreateLHCClose,
  } = useDisclosure();

  // Delete LHC state
  const [lhcToDelete, setLhcToDelete] = useState<LHCRowData | null>(null);
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);

  // Toast
  const toast = useToast();

  // Export mutation
  const { exportLHC, isExporting } = useLHCExportMutation({
    onError: (error) => {
      showToast(toast, 'error', error.message || 'Gagal mengekspor LHC');
    },
  });

  // Delete mutation
  const deleteMutation = useDeleteLHCMutation({
    onSuccess: () => {
      showToast(toast, 'success', 'LHC berhasil dihapus');
      onDeleteClose();
      setLhcToDelete(null);
    },
    onError: (error) => {
      showToast(toast, 'error', error.message || 'Gagal menghapus LHC');
    },
  });

  const handleEditLHCOpen = useCallback(
    (row: LHCRowData) => {
      setSelectedLHCForEdit(row);
      onEditLHCOpen();
    },
    [onEditLHCOpen]
  );

  const handleEditLHCClose = useCallback(() => {
    setSelectedLHCForEdit(null);
    onEditLHCClose();
  }, [onEditLHCClose]);

  const handleCreateOpen = useCallback(() => {
    onCreateLHCOpen();
  }, [onCreateLHCOpen]);

  const handleCreateLHCClose = useCallback(() => {
    onCreateLHCClose();
  }, [onCreateLHCClose]);

  // Delete handlers
  const handleDeleteOpen = useCallback(
    (row: LHCRowData) => {
      setLhcToDelete(row);
      onDeleteOpen();
    },
    [onDeleteOpen]
  );

  const handleConfirmDelete = useCallback(() => {
    if (lhcToDelete) {
      deleteMutation.mutate(lhcToDelete.id);
    }
  }, [deleteMutation, lhcToDelete]);

  const columnDefs = useMemo<ColDef<LHCRowData>[]>(
    () => [
      {
        field: 'no',
        headerName: 'No.',
        width: 60,
        pinned: 'left',
        resizable: false,
      },
      { field: 'lhcDate', headerName: 'Tgl. LHC', width: 110, pinned: 'left' },
      {
        field: 'lhcNo',
        headerName: 'No. LHC',
        width: 160,
        pinned: 'left',
        cellStyle: { color: '#0000A3', cursor: 'pointer', fontWeight: 500 },
      },
      { field: 'plant', headerName: 'Plant', minWidth: 80, flex: 1 },
      {
        field: 'totalInvoice',
        headerName: 'Total Faktur',
        minWidth: 100,
        flex: 1,
      },
      {
        field: 'totalInvoiceValue',
        headerName: 'Total Nilai Faktur',
        minWidth: 150,
        flex: 1,
      },
      {
        field: 'mutationValue',
        headerName: 'Nilai Mutasi',
        minWidth: 150,
        flex: 1,
      },
      {
        field: 'totalDifference',
        headerName: 'Total Selisih +/-',
        minWidth: 140,
        flex: 1,
      },
      {
        field: 'lastUpdated',
        headerName: 'Terakhir diperbarui',
        minWidth: 160,
        flex: 1,
      },
      {
        colId: 'actions',
        headerName: 'Action',
        width: 130,
        pinned: 'right',
        cellRenderer: ActionCellRenderer,
        cellRendererParams: {
          onExportLHC: exportLHC,
          isExporting,
          onEditLHC: handleEditLHCOpen,
          onDeleteLHC: handleDeleteOpen,
        } as ActionCellRendererParams,
        sortable: false,
        filter: false,
        resizable: false,
      },
    ],
    [exportLHC, isExporting, handleEditLHCOpen, handleDeleteOpen]
  );

  return (
    <AdminLayout>
      <Box paddingBottom={4}>
        <Breadcrumbs links={links} useRouterDom />
      </Box>
      <Box py={4}>
        <Flex justify="space-between" align="center" mb={6}>
          <Flex align="center" gap={3}>
            <IconButton
              aria-label="Kembali"
              icon={<ChevronLeft sx={{ fontSize: 24 }} />}
              variant="ghost"
              size="sm"
              onClick={navigateHome}
            />
            <Text as="h5" fontSize="32px" fontWeight="bold">
              Laporan Harian Collection
            </Text>
          </Flex>
          <Box maxW="453px">
            <SyncInfo lastSyncedAt={undefined} onSync={() => refetch()} />
          </Box>
        </Flex>
        <Divider mb={4} />

        {/* Filter Bar with Buat LHC button and filters */}
        <Flex
          mb={4}
          gap={3}
          alignItems={{ base: 'stretch', md: 'center' }}
          justifyContent="space-between"
          flexWrap="wrap"
        >
          {/* Left side: Buat LHC button + filters */}
          <Flex
            gap={3}
            width="full"
            alignItems={{ base: 'stretch', md: 'center' }}
            flexWrap="wrap"
          >
            {/* Filters */}
            <CollectionFilterBar
              methods={methods}
              searchValue={searchValue}
              onSearchChange={handleSearchChange}
              onClearSearch={handleClearSearch}
              handleSearch={handleSearch}
              handleDateFilter={handleDateFilter}
              updateDateParams={updateDateParams}
              plantValue={draftFilters.plantValue}
              onPlantChange={handlePlantChange}
              plantOptions={plantOptions}
              cabangValue={draftFilters.cabangValue}
              onCabangChange={handleCabangChange}
              onCreateLHC={handleCreateOpen}
            />
          </Flex>
        </Flex>

        <Card p="0">
          <GridTable<LHCRowData>
            columnDefs={columnDefs}
            rowData={tableData}
            isLoading={isLoading}
            pagination={false}
            onRowClick={(data) => {
              if (data?.id) {
                navigateToDetail(data.id);
              }
            }}
            suppressRowClickForColumns={['actions']}
            gridOptions={{
              defaultColDef: {
                resizable: true,
                sortable: true,
              },
              domLayout: 'normal',
            }}
          />
        </Card>

        {/* External Pagination */}
        <Flex justify="center" align="center" gap={4} mt={4}>
          <Pagination
            currentPage={validCurrentPage}
            totalPage={totalPages}
            nextPage={nextPage}
            previousPage={previousPage}
            firstPage={firstPage}
            lastPage={lastPage}
          />
          <Flex align="center" gap={2} fontSize="14px">
            <ChakraText>per halaman</ChakraText>
            <Select
              size="sm"
              width="80px"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              borderColor="gray.33"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </Flex>
        </Flex>
      </Box>

      {/* Duplicate flow intentionally disabled until backend release-ready. */}
      <CreateLHCModal
        isOpen={isCreateLHCOpen}
        onClose={handleCreateLHCClose}
        plantOptions={plantOptions}
        onSync={() => refetchCollectionPlants()}
      />

      <EditLHCModal
        isOpen={isEditLHCOpen}
        onClose={handleEditLHCClose}
        initialData={
          selectedLHCForEdit
            ? {
                id: selectedLHCForEdit.id,
                lhcNo: selectedLHCForEdit.lhcNo,
                lhcDateRaw: selectedLHCForEdit.lhcDateRaw,
                collectionPlantId: selectedLHCForEdit.collectionPlantId,
                updatedAtUnix: selectedLHCForEdit.updatedAtUnix,
              }
            : null
        }
        plantOptions={plantOptions}
        lastSyncedAt={selectedLHCForEdit?.updatedAtUnix}
        onSync={() => refetchCollectionPlants()}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus LHC?
            </AlertDialogHeader>

            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus LHC &quot;{lhcToDelete?.lhcNo}
              &quot;? Tindakan ini tidak dapat dibatalkan.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelDeleteRef}
                onClick={onDeleteClose}
                variant="outline"
              >
                Batal
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                isLoading={deleteMutation.isPending}
                ml={3}
              >
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </AdminLayout>
  );
}

export default CollectionManagement;
